import React, {FC} from 'react'
import {Controller, useFieldArray, useFormContext} from 'react-hook-form'
import ArrowDown from '@pixel/assets/icons/SortDown'
import TextField from '@pixel/core/TextField'
import { styled } from '@pixel/core/styles'
import { useMultipleLocalizedText } from '../../contexts/Localization/localizationHooks'
import IconButton from "@pixel/core/IconButton";
import DeleteIcon from "@pixel/assets/icons/Delete";
import Button from "@pixel/core/Button";
import Typography from "@pixel/core/Typography";
import {Box} from "@pixel/core";
import LocalizedTypography from "../../components/LocalizedTypography";
import {LocalizedText} from "../../contexts/Localization/LocalizationTypes";

const FormSectionContainer = styled('div')(({ theme }) => ({
    backgroundColor: 'white',
    borderRadius: '8px',
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}))

const FieldListFormContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
})

const FormHeaderSection = styled('div')(({ theme }) => ({
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingBottom: theme.spacing(3),
}))

const FormBodySection = styled('div')({
    backgroundColor: '#e4edf3',
    flexGrow: 1,
})

const ArrowDisplay = styled(ArrowDown)(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(3),
}))

const AddFieldListValueContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'end',
    marginTop: theme.spacing(1),
}))

interface FieldListFormProps {
}

const FieldListForm: FC<FieldListFormProps> = ({
}) => {
    const {
        FIELD_LIST_FORM_VALUE,
        FIELD_LIST_FORM_NAME,
        FIELD_LIST_ADD_VALUE
    } = useMultipleLocalizedText()

    const { control, watch, setValue } = useFormContext()

    const { fields, append, remove } = useFieldArray({
        control,
        name: "fieldListValues",
    });

    const valueToBeAdded = watch("value");

    const addValue = () => {
        if (valueToBeAdded) {
            append({ value: valueToBeAdded });
            setValue('value', '')
        }
    };

    return (
        <FieldListFormContainer>
            <FormHeaderSection>
                <Controller
                    control={control}
                    name="name"
                    rules={{ maxLength: 100, required: true, }}
                    render={({ field, fieldState, }) => (
                        <TextField
                            id="fieldListForm-name"
                            required
                            label={FIELD_LIST_FORM_NAME}
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            error={fieldState.invalid}
                            errorText={fieldState?.error?.message || ''}
                            fullWidth
                            sx={{ marginBottom: 1 }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="status"
                    rules={{ required: true }}
                    defaultValue={"ACTIVE"}
                    render={() => (
                        <TextField
                            value={"ACTIVE"}
                            hidden={true}
                        >
                        </TextField>
                    )}
                />
            </FormHeaderSection>
            <FormBodySection>
                <FormSectionContainer>
                    <Controller
                        control={control}
                        name="value"
                        render={({ field }) => (
                            <TextField
                                id="fieldListForm-newValue"
                                {...field}
                                label={FIELD_LIST_FORM_VALUE}
                                fullWidth
                                disabled={fields.length >= 750}
                            />
                        )}
                    />
                    {fields.length >= 750 &&
                        <LocalizedTypography variant="body1" color={"red"} marginTop={1} text={LocalizedText.FIELD_LIST_MAXIMUM_CAPACITY} />
                    }
                    <AddFieldListValueContainer>
                        <Button onClick={addValue} variant="outlined" color="primary">
                            {FIELD_LIST_ADD_VALUE}
                        </Button>
                    </AddFieldListValueContainer>
                </FormSectionContainer>
                {fields.length > 0 &&
                    <>
                        <ArrowDisplay />

                        <FormSectionContainer>
                            {fields.map((field, index) => (
                                <Box key={index}  display="flex" alignItems="center">
                                    <IconButton size={"small"} onClick={() => remove(index)}>
                                        <DeleteIcon fontSize={"medium"} color="primary" />
                                    </IconButton>
                                    <Typography data-id={`fieldListForm-${(field as any).value}-value`} variant="body1" marginLeft={1}>{(field as any).value}</Typography>
                                </Box>
                            ))}
                        </FormSectionContainer>
                    </>
                }
            </FormBodySection>
        </FieldListFormContainer>
    )
}
export default FieldListForm
